import { useEffect } from 'react';
import { ArrowLeft, ArrowRight } from 'lucide-react';
import { Navigation, type Locale } from './components/Navigation';
import { SiteFooter } from './components/SiteFooter';

export default function NotFoundPage() {
  const locale: Locale = window.location.pathname.startsWith('/en/') ? 'en' : 'sv';
  const homePath = locale === 'en' ? '/en/' : '/';

  useEffect(() => {
    document.documentElement.lang = locale === 'sv' ? 'sv-SE' : 'en';
    document.title = locale === 'sv' ? 'Sidan hittades inte | Keevo' : 'Page not found | Keevo';
  }, [locale]);

  const changeLocale = (nextLocale: Locale) => {
    if (nextLocale === locale) return;
    window.location.assign(nextLocale === 'en' ? '/en/' : '/');
  };

  const copy = locale === 'sv'
    ? {
        eyebrow: '404 · Sidan hittades inte',
        title: 'Den här adressen leder inte till en sida på Keevo.',
        body: 'Länken kan vara gammal, felstavad eller ha flyttats. Använd navigationen eller gå tillbaka till startsidan.',
        home: 'Till startsidan',
        services: 'Se tjänster',
      }
    : {
        eyebrow: '404 · Page not found',
        title: 'This address does not lead to a page on Keevo.',
        body: 'The link may be outdated, mistyped or moved. Use the navigation or return to the home page.',
        home: 'Back to home',
        services: 'View services',
      };

  return (
    <div className="min-h-screen bg-[#F4F3EF] text-[#12141A]">
      <Navigation locale={locale} onLocaleChange={changeLocale} />

      <main className="px-6 pb-28 pt-44 md:px-8 lg:px-16 lg:pb-36 lg:pt-52">
        <div className="mx-auto max-w-[1100px]">
          <span className="text-xs font-bold uppercase tracking-[0.14em] text-[#5B4BFF]">{copy.eyebrow}</span>
          <h1 className="mt-6 max-w-[900px] font-sora text-5xl font-extrabold leading-[1.02] tracking-[-0.05em] md:text-7xl">
            {copy.title}
          </h1>
          <p className="mt-7 max-w-[680px] text-lg leading-8 text-[#62656E]">{copy.body}</p>
          <div className="mt-9 flex flex-wrap gap-3">
            <a href={homePath} className="inline-flex min-h-12 items-center gap-2 rounded-full bg-[#5B4BFF] px-6 font-bold text-white transition hover:bg-[#4B37E0]">
              <ArrowLeft size={18} /> {copy.home}
            </a>
            <a href={`${homePath}#tjanster`} className="inline-flex min-h-12 items-center gap-2 rounded-full border border-[#D8D5DD] bg-white px-6 font-bold text-[#4F525B] transition hover:border-[#BEB7F4] hover:text-[#5B4BFF]">
              {copy.services} <ArrowRight size={18} />
            </a>
          </div>
        </div>
      </main>

      <SiteFooter locale={locale} />
    </div>
  );
}
